import { useEffect, useState, useRef } from "react"
import { supabase } from "../supabaseClient"
import { Card } from "./ui/card"
import { getActiveYear } from "../utils/activeYear"
import { calculateTeamPoints } from "../utils/scoring"

export interface TeamForModal {
  id: number
  name: string
  seed: number
  is_eliminated: boolean
  pickCount?: number
}

export interface EntryForModal {
  id: string
  entry_name: string
  points: number
  teamsAlive: number
  display_name?: string
  leaderboardRank?: number
}

interface TeamPickersModalProps {
  team: TeamForModal
  onClose: () => void
  onEntryClick?: (entryId: string) => void
}

export function TeamPickersModal({ team, onClose, onEntryClick }: TeamPickersModalProps) {
  const [entries, setEntries] = useState<EntryForModal[]>([])
  const [loading, setLoading] = useState(true)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartY = useRef(0)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    async function loadEntries() {
      setLoading(true)
      
      const year = await getActiveYear()

      // Get all entry_picks for this team with entry info
      const { data: picksData } = await supabase
        .from('entry_picks')
        .select('entry_id, entry:entries!inner(id, entry_name, user_id, year)')
        .eq('team_id', team.id)
        .eq('entry.year', year)

      if (!picksData || picksData.length === 0) {
        setEntries([])
        setLoading(false)
        return
      }

      // Get unique entry IDs
      const entryIds = Array.from(new Set(
        picksData.map((p: any) => {
          const entry = Array.isArray(p.entry) ? p.entry[0] : p.entry
          return entry?.id
        }).filter(Boolean)
      ))

      // Get ALL entries for this year to calculate global leaderboard rankings
      const { data: allEntriesData } = await supabase
        .from('entries')
        .select('id')
        .eq('year', year)

      const allEntryIds = allEntriesData?.map((e: any) => e.id) || []

      // Get all picks for ALL entries to calculate global points
      const { data: allPicksData } = await supabase
        .from('entry_picks')
        .select('entry_id, team:tournament_teams!inner(id, seed, wins, is_eliminated)')
        .in('entry_id', allEntryIds)

      // Calculate points and teams alive per entry (for all entries)
      const entryPointsMap = new Map<string, number>()
      const entryAliveMap = new Map<string, number>()
      
      allPicksData?.forEach((pick: any) => {
        const pickTeam = Array.isArray(pick.team) ? pick.team[0] : pick.team
        if (pickTeam) {
          // Points
          const currentPoints = entryPointsMap.get(pick.entry_id) || 0
          const teamPoints = calculateTeamPoints(pickTeam.seed || 1, pickTeam.wins || 0)
          entryPointsMap.set(pick.entry_id, currentPoints + teamPoints)
          
          // Teams alive
          if (!pickTeam.is_eliminated) {
            const currentAlive = entryAliveMap.get(pick.entry_id) || 0
            entryAliveMap.set(pick.entry_id, currentAlive + 1)
          }
        }
      })

      // Calculate global leaderboard rankings
      const allEntriesWithPoints = allEntryIds.map((id: string) => ({
        id,
        points: entryPointsMap.get(id) || 0
      })).sort((a: {id: string, points: number}, b: {id: string, points: number}) => b.points - a.points)

      // Assign ranks (handling ties)
      const rankMap = new Map<string, number>()
      let currentRank = 1
      for (let i = 0; i < allEntriesWithPoints.length; i++) {
        if (i > 0 && allEntriesWithPoints[i].points < allEntriesWithPoints[i - 1].points) {
          currentRank = i + 1
        }
        rankMap.set(allEntriesWithPoints[i].id, currentRank)
      }

      // Get unique user IDs
      const userIds = Array.from(new Set(
        picksData.map((p: any) => {
          const entry = Array.isArray(p.entry) ? p.entry[0] : p.entry
          return entry?.user_id
        }).filter(Boolean)
      ))

      // Fetch profiles
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', userIds)

      const profileMap = new Map<string, string>()
      profilesData?.forEach((p: any) => {
        if (p.display_name) profileMap.set(p.id, p.display_name)
      })

      // Combine data with leaderboard rankings
      const entriesList = picksData.map((p: any) => {
        const entry = Array.isArray(p.entry) ? p.entry[0] : p.entry
        return {
          id: entry.id,
          entry_name: entry.entry_name,
          points: entryPointsMap.get(entry.id) || 0,
          teamsAlive: entryAliveMap.get(entry.id) || 0,
          display_name: profileMap.get(entry.user_id),
          leaderboardRank: rankMap.get(entry.id) || 999
        }
      }).sort((a: EntryForModal, b: EntryForModal) => b.points - a.points)

      setEntries(entriesList)
      setLoading(false)
    }

    loadEntries()
  }, [team.id])

  // Handle drag to dismiss - ONLY from handle area
  const handleHandleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY
    setIsDragging(true)
  }

  const handleHandleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return
    const currentY = e.touches[0].clientY
    const diff = currentY - dragStartY.current
    // Only allow dragging down (positive offset)
    if (diff > 0) {
      setDragOffset(diff)
    }
  }

  const handleHandleTouchEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    // If dragged more than 100px, close the modal
    if (dragOffset > 100) {
      onClose()
    } else {
      setDragOffset(0)
    }
  }

  const handleEntryClick = (entryId: string) => {
    if (onEntryClick) {
      onEntryClick(entryId)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose}>
      {/* Bottom Sheet Container */}
      <div 
        ref={sheetRef}
        className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl flex flex-col
          max-h-[360px] sm:max-h-[70vh]
          ${isDragging ? '' : 'transition-transform duration-200'}
          ${dragOffset === 0 && !isDragging ? 'animate-slide-up' : ''}`}
        style={{ transform: `translateY(${dragOffset}px)` }}
        onClick={e => e.stopPropagation()}
        // Remove touch handlers from here - they're now only on the handle
      >
        {/* Drag Handle - touch events only here */}
        <div 
          className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
          onTouchStart={handleHandleTouchStart}
          onTouchMove={handleHandleTouchMove}
          onTouchEnd={handleHandleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>
        
        {/* Header */}
        <div className="px-4 pb-3 flex items-center justify-between border-b border-gray-100">
          <div>
            <div className={`font-bold text-lg ${team.is_eliminated ? 'text-gray-400' : 'text-gray-900'}`}>
              {team.name}
            </div>
            <div className="text-gray-400 text-sm">
              {team.seed} seed • {entries.length} {entries.length === 1 ? 'entry' : 'entries'} picked
              {team.is_eliminated && <span className="ml-1 text-red-400">• Eliminated</span>}
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content - Scrollable (no data-scrollable needed anymore) */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading entries...</div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No entries have picked this team</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {entries.map((entry) => (
                <button
                  key={entry.id}
                  onClick={() => handleEntryClick(entry.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    onEntryClick ? 'hover:bg-gray-50 active:bg-gray-100 cursor-pointer' : ''
                  }`}
                  disabled={!onEntryClick}
                >
                  {/* Leaderboard Rank */}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    entry.leaderboardRank === 1 ? 'bg-amber-400 text-amber-900' :
                    entry.leaderboardRank === 2 ? 'bg-gray-300 text-gray-700' :
                    entry.leaderboardRank === 3 ? 'bg-amber-600 text-white' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {entry.leaderboardRank}
                  </div>

                  {/* Entry Name & Player */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#1e3a5f] truncate">{entry.entry_name}</div>
                    <div className="text-gray-400 text-xs truncate">{entry.display_name || 'Player'}</div>
                  </div>

                  {/* Teams Alive */}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    entry.teamsAlive > 10 
                      ? 'bg-emerald-50 text-emerald-600' 
                      : entry.teamsAlive > 5 
                        ? 'bg-amber-50 text-amber-600'
                        : 'bg-rose-50 text-rose-500'
                  }`}>
                    {entry.teamsAlive}
                  </span>

                  {/* Points */}
                  <div className="font-bold text-[#1e3a5f]">{entry.points}</div>
                  
                  {onEntryClick && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-300 flex-shrink-0">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Safe area padding for iOS */}
        <div className="h-safe-area-inset-bottom" />
      </div>
    </div>
  )
}
